import React from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import VariableMentionPopup from '../../../containers/EmailDesignPage/EmailBuilder/VariableMentionPopup';
import { Icon, Popover, Select, Tooltip, message } from 'antd';
import {
  DateVar,
  PopupActionContainer,
  PopupContainer,
  PopupLabel,
  CancelButton,
  SaveButton,
} from '../../../artboard/Page/styles';
import VariableMention from '../../../artboard/Page/VariableModule';
import MentionEmbed from '../../../artboard/Page/MentionEmbed';
import { withTranslation } from 'react-i18next';
import { SHORT_MESSAGE_CHANNELS, UPDATE_DATE_VARIABLE_FORMAT_POPUP } from './translationValues';
import { Clipboard } from '../../../utils/helper';

const NAMESPACES = ['UPDATE_DATE_VARIABLE_FORMAT_POPUP', 'SHORT_MESSAGE_CHANNELS']
const { Option } = Select;

// Custom style attributes
let Parchment = Quill.import('parchment');
const blockConfig = {
  scope: Parchment.Scope.BLOCK,
  whitelist: null,
};
const inlineConfig = {
  scope: Parchment.Scope.INLINE,
  whitelist: null,
};

const Delta = Quill.import('delta');

/* This is quill editing area - handles both text and variables editing
  Child Components: 
    * VariableMentionPopup
*/

class ShortMessageEditingArea extends React.PureComponent {
  static modules = (
    toggleVariablePopup = () => {},
    toggleConversionPopup = () => {},
    editorId,
  ) => ({
    toolbar:
      editorId === 0
        ? {
            container: '#toolbar-shortMsg',
          }
        : false,
    variableMention: {
      mentionDenotationChars: ['$'],
      disabled: false,
      uuid: undefined,
      toggleVariablePopup,
      toggleConversionPopup,
      isEmail: false,
    },
  });

  static formats = ['bold', 'italic', 'underline', 'link', 'variablehighlighter', 'mention'];

  reactQuillRef = null;

  constructor(props) {
    super(props);

    // Inline CSS Styles configuration in editor
    const BackgroundClass = Quill.import('attributors/style/background');
    const ColorClass = Quill.import('attributors/style/color');
    const SizeStyle = Quill.import('attributors/style/size');
    const AlignStyle = Quill.import('attributors/style/align');
    Quill.register(BackgroundClass, true);
    Quill.register(ColorClass, true);
    Quill.register(SizeStyle, true);
    Quill.register(AlignStyle, true);

    // Add sizes to whitelist
    let Size = Quill.import('attributors/style/size');
    Size.whitelist = null;
    Quill.register(Size, true);

    var Bold = Quill.import('formats/bold');
    Bold.tagValue = '700'; // Quill uses <strong> by default
    Quill.register(Bold, true);

    const fontFamilyClass = new Parchment.Attributor.Class('font', 'ql-font-family', blockConfig);
    const fontFamilyStyle = new Parchment.Attributor.Style('font', 'font-family', blockConfig);
    // const fontFamilyStyle = FontStyle;
    Parchment.register(fontFamilyClass);
    Parchment.register(fontFamilyStyle);
    Quill.register(fontFamilyStyle, true);

    const fontSizeClass = new Parchment.Attributor.Class('size', 'ql-size', blockConfig);
    const fontSizeStyle = new Parchment.Attributor.Style('size', 'font-size', blockConfig);
    Parchment.register(fontSizeClass);
    Parchment.register(fontSizeStyle);
    const lineHtClass = new Parchment.Attributor.Class('lineheight', 'ql-lineheight', blockConfig);
    const lineHtStyle = new Parchment.Attributor.Style('lineheight', 'line-height', blockConfig);
    Parchment.register(lineHtClass);
    Parchment.register(lineHtStyle);
    const letterspaceClass = new Parchment.Attributor.Class(
      'letterspace',
      'ql-letterspace',
      inlineConfig,
    );
    const letterspaceStyle = new Parchment.Attributor.Style(
      'letterspace',
      'letter-spacing',
      inlineConfig,
    );
    Parchment.register(letterspaceClass);
    Parchment.register(letterspaceStyle);

    Quill.register("modules/variableMention", VariableMention);
    Quill.register(MentionEmbed);
    Quill.register('modules/clipboard', Clipboard, true);

    this.state = {
      showMentionPopup: false,
      showConversionPopup: false,
      variableTerm: '',
      popupPosition: undefined,
      isInputChanged: false,
      inputHTML: '',
      varTooltipVisible: false,
    };
    this.nodeRef = React.createRef();
  }

  componentDidMount() {
    const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;
    quill.blur();
    this.dateFormatOption();
    quill.enable(this.props.quillEnabled);
    if (this.props.shortMsgText) {
      this.setState({
        inputHTML: this.props.shortMsgText,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.shortMsgText !== prevProps.shortMsgText) {
      this.setState({
        inputHTML: this.props.shortMsgText,
      });
    }
    if (this.props.currentVersion !== prevProps.currentVersion) {
      this.setState({ editorHtml: this.props.text });
    }
    if (prevProps.quillEnabled !== this.props.quillEnabled) {
      // Retained this logic to unfocus quill on change of editor
      const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;
      quill.enable(this.props.quillEnabled);
    }
    if (prevProps.saveChanges !== this.props.saveChanges) {
      if (this.props.saveChanges) {
        const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;
        quill.blur();
        this.setState({ isInputChanged: false });
      }
    }
  }

  toggleVariablePopup = (show, variableTerm, position) => {
    this.setState({
      showMentionPopup: show,
      variableTerm,
      popupPosition: show ? position : this.state.popupPosition,
    });
  };

  onMentionPopupHide = () => {
    this.setState({
      showMentionPopup: false,
    });
  };

  onVariableSelect = data => {
    const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;
    quill
      .getModule('variableMention')
      .onVariableSelect(
        data.displayName ? data.displayName : data.placeHolderText,
        data.id,
        data.length,
        'assigned',
        data.type,
        data.count,
        false,
        true,
        data.placeHolderText,
      );
    this.setState({
      showMentionPopup: false,
    });
  };

  toggleConversionPopup = (show, position) => {
    this.setState({
      showConversionPopup: show,
      popupPosition: show ? position : this.state.popupPosition,
    });
  };

  dateFormatOption = () => {
    const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;
    let targetElement = quill?.scrollingContainer;
    if (targetElement) {
      targetElement.addEventListener('mouseover', this.dateVariable);
      if (targetElement.parentNode) {
        targetElement.parentNode.addEventListener('mouseenter', this.removeDateFormat);
      }
    }
  };

  removeDateFormat = () => {
    if (!this.state.editIconClicked) {
      this.setState({
        selectedDateVar: '',
      });
    }
  };

  dateVariable = event => {
    const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;
    let targetElement = quill?.scrollingContainer;
    let variableData = {};
    let target = event.target;
    let dateVariable = '';
    const elementPos = targetElement.getBoundingClientRect();
    if (target?.getAttribute('data-variable-type') === 'Date') {
      dateVariable = target;
    } else if (target?.parentNode?.getAttribute('data-variable-type') === 'Date') {
      dateVariable = target.parentNode;
    }
    if (dateVariable) {
      this.setState({ selectedDateVar: '' });
      let variablePos = dateVariable.getBoundingClientRect();
      variableData.posY = variablePos.y - elementPos.y + 10;
      variableData.posX = variablePos.x - elementPos.x;
      variableData.height = variablePos.height + 8;
      variableData.width = variablePos.width;
      variableData.id = dateVariable.getAttribute('data-id');
      variableData.dateFormat = dateVariable.getAttribute('data-date-format');
      variableData.value = dateVariable.getAttribute('data-value');
      variableData.placeHolderText = dateVariable.getAttribute('data-place-holder-text');
      variableData.element = dateVariable;
      variableData.nodeData = dateVariable.dataset;
      this.setState({ selectedDateVar: variableData, targetElement: dateVariable });
    } else {
      this.setState({
        selectedDateVar: this.state.editIconClicked ? this.state.selectedDateVar : '',
      });
    }
  };

  changeDateFormat = (format, data) => {
    const { targetElement } = this.state;
    const existingElement = Quill.find(targetElement);
    this.quillRef = this.reactQuillRef.getEditor();
    const position = this.quillRef.getIndex(existingElement);
    this.quillRef.deleteText(position, 1);
    this.quillRef.insertEmbed(
      position,
      'mention',
      Object.assign({}, data.nodeData, { dateFormat: format }),
    );
    this.reactQuillRef.focus();
    this.setState({ isInputChanged: true, selectedDateVar: '' });
  };

  handleInputChange = (html, delta, source, editor) => {
    const { updateDocumentVariables, maxCharLength, changeCount, t, editorId } = this.props;
    let variablesList = [];
    var inputChanged = false;
    const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;
    let excess = editorId === 0 ? -1 : 0;
    if (editorId !== 0 || (maxCharLength && editor && quill)) {
      const currentLength = editor.getLength() - 1;
      let netChange = 0;
      if (delta && delta.ops) {
        delta.ops.forEach(op => {
          if (op.insert) {
            netChange += (typeof op.insert === 'string' ? op.insert.length : 1);
          } else if (op.delete) {
            netChange -= op.delete;
          }
        });
      }
      const previousLength = currentLength - netChange;
      let allowedLength = maxCharLength;
      if (previousLength > maxCharLength) {
        allowedLength = previousLength;
      }
      excess = currentLength - allowedLength;
      if (excess > 0) {
        const ops = delta.ops;
        const insertions = [];
        let index = 0;
        ops.forEach(op => {
          if (op.retain) {
            index += op.retain;
          } else if (op.insert) {
            const len = typeof op.insert === 'string' ? op.insert.length : 1;
            insertions.push({ index, length: len });
            index += len;
          }
        });
        for (let i = insertions.length - 1; i >= 0; i--) {
          if (excess <= 0) break;
          const { index: insertIndex, length: insertLength } = insertions[i];
          const toRemove = Math.min(excess, insertLength);
          quill.deleteText(insertIndex + insertLength - toRemove, toRemove);
          excess -= toRemove;
        }
        source === 'user' && message.error(t(`SHORT_MESSAGE_CHANNELS:${SHORT_MESSAGE_CHANNELS.CHAR_LIMIT_EXCEEDED_ERROR.key}`, SHORT_MESSAGE_CHANNELS.CHAR_LIMIT_EXCEEDED_ERROR.value));
      }
    }

    if ((source === 'user' || source === 'api') && delta && delta.ops && delta.ops.length > 0) {
      inputChanged = true;
      const finalHtml = quill ? quill.root.innerHTML : html;
      this.setState({
        isInputChanged: inputChanged,
        inputHTML: finalHtml,
      });
      if (quill) {
        const srcArray = quill.getContents().ops;
        for (var index = 0; index < srcArray.length; ++index) {
          var dataByIndex = srcArray[index];
          if (dataByIndex?.insert?.mention) {
            var varData = dataByIndex?.insert?.mention;
            const variable = {
              value: varData.value,
              variableId: parseInt(varData.id),
            };
            variablesList.push(variable);
          }
        }
      }
      const textLength = quill ? (quill.getLength() > 0 ? quill.getLength() - 1 : 0) : 0;
      changeCount(textLength, inputChanged);
      if (updateDocumentVariables) {
        updateDocumentVariables(variablesList);
      }
    }
  };

  handleEditorBlur = (html, source) => {
    const { showMentionPopup, inputHTML, isInputChanged } = this.state;
    const { onEditorFocus, updateShortMsgContent } = this.props;
    if (source === 'user' && !showMentionPopup) {
      onEditorFocus(null);
      if (inputHTML) {
        if (inputHTML && isInputChanged) {
          const { editorId } = this.props;
          let element = document?.getElementsByClassName(`ql-editor-area-${editorId}`);
          if (element?.length > 0) {
            let htmlText = element[0].getElementsByClassName(`ql-editor`);
            updateShortMsgContent(htmlText[0].childNodes);
            this.setState({ isInputChanged: false });
          }
        }
      }
      setTimeout(() => {
        this.toggleVariablePopup(false);
        this.setState({
          showMentionPopup: false,
        });
      }, 10);
    }
  };

  handleCancelButton = () => {
    this.reactQuillRef.focus();
    this.setState({ selectedDateVar: '', editIconClicked: false });
  };
  
  handleUpdateGlobalVariables = async (newVariable) => {
    return await this.props.updateGlobalVariables(newVariable, 'addNewVariable');
  }

  render() {
    const { editorId, placeHolder, showVersions, globalVariables, updateGlobalVariables, t } = this.props;
    const {
      popupPosition,
      showMentionPopup,
      variableTerm,
      selectedDateVar,
      inputHTML,
      changedFormat,
      editIconClicked,
    } = this.state;
    const quill = this.reactQuillRef ? this.reactQuillRef.getEditor() : null;

    return (
      <div className={`ql-editor-area-${editorId}`} data-uuid={editorId} ref={this.nodeRef}>
        <ReactQuill
          ref={el => {
            this.reactQuillRef = el;
          }}
          onChange={this.handleInputChange}
          onBlur={this.handleEditorBlur}
          value={inputHTML || ''}
          modules={ShortMessageEditingArea.modules(
            this.toggleVariablePopup,
            this.toggleConversionPopup,
            editorId,
          )}
          formats={ShortMessageEditingArea.formats}
          placeholder={placeHolder}
        />
        {showMentionPopup && (
          <VariableMentionPopup
            quill={quill}
            searchTerm={variableTerm}
            onVariableSelect={this.onVariableSelect}
            onHide={this.onMentionPopupHide}
            position={popupPosition}
            uuid={editorId}
            globalVariables={globalVariables} 
            updateGlobalVariables={this.handleUpdateGlobalVariables}
          />
        )}
        {!showVersions && selectedDateVar && (
          <Tooltip 
            title={selectedDateVar?.placeHolderText} 
            overlayClassName={'date-var-tooltip'}
            visible={this.state.varTooltipVisible}
            onVisibleChange={(e) => this.setState({ varTooltipVisible: e })}>
          <DateVar
            id="#dateVar"
            isEmail={false}
            messageStudio={1}
            style={{
              height: selectedDateVar.height,
              width: selectedDateVar.width,
              transform: `translate3d(${selectedDateVar.posX}px, ${selectedDateVar.posY}px, 0px)`,
            }}
          >
            <Popover
              title=""
              trigger="click"
              content={
                <PopupContainer>
                  <PopupLabel>{selectedDateVar?.placeHolderText}</PopupLabel>
                  <PopupLabel> {t(UPDATE_DATE_VARIABLE_FORMAT_POPUP.DATE_FORMAT_LABEL.key, UPDATE_DATE_VARIABLE_FORMAT_POPUP.DATE_FORMAT_LABEL.value)} </PopupLabel>
                  <Select
                    labelInValue
                    defaultValue={{ key: selectedDateVar?.dateFormat }}
                    style={{ width: '100%' }}
                    onChange={e => {
                      this.setState({ changedFormat: e.key });
                    }}
                  >
                    <Option value="dd-MM-yy">dd-MM-yy</Option>
                    <Option value="dd-MM-yyyy">dd-MM-yyyy</Option>
                    <Option value="MM-dd-yyyy">MM-dd-yyyy</Option>
                    <Option value="yyyy-MM-dd">yyyy-MM-dd</Option>
                    <Option value="dd-MMM-yyyy">dd-MMM-yyyy</Option>
                    <Option value="EEEEE MMMMM yyyy">EEEEE MMMMM yyyy</Option>
                  </Select>
                  <PopupActionContainer>
                    <CancelButton size="default" onClick={() => this.handleCancelButton()}>
                    {t(UPDATE_DATE_VARIABLE_FORMAT_POPUP.CANCEL_UPDATE_DATE_VARIABLE_FORMAT_BUTTON.key, UPDATE_DATE_VARIABLE_FORMAT_POPUP.CANCEL_UPDATE_DATE_VARIABLE_FORMAT_BUTTON.value)}
                    </CancelButton>
                    <SaveButton
                      type="primary"
                      size="default"
                      onClick={() => {
                        this.changeDateFormat(changedFormat, selectedDateVar);
                      }}
                    >
                      {t(UPDATE_DATE_VARIABLE_FORMAT_POPUP.SAVE_UPDATE_DATE_VARIABLE_FORMAT_BUTTON.key, UPDATE_DATE_VARIABLE_FORMAT_POPUP.SAVE_UPDATE_DATE_VARIABLE_FORMAT_BUTTON.value)}
                    </SaveButton>
                  </PopupActionContainer>
                </PopupContainer>
              }
            >
              <div className={'edit-button'} onClick={() => {
                  this.setState({ varTooltipVisible: false, editIconClicked: !editIconClicked });
                }}>
                  <Icon type="edit" />
              </div>
            </Popover>
          </DateVar>
          </Tooltip>
        )}
      </div>
    );
  }
}

export default withTranslation(NAMESPACES)(ShortMessageEditingArea);